import { DOCUMENT } from '@angular/common';
import { AfterViewInit, Component, Inject, Input, OnInit } from '@angular/core';
import { AccessibilityService } from '@core-mkt/services/accessibility/accessibility.service';
import { SchemaService } from '@core-mkt/services/schema/schema.service';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { QueryParamDirective } from '@core-mkt/services/query-param/query-param.directive';
import { Breadcrumb, BreadcrumbItem } from './breadcrumb';
import { getBreadcrumbSchema } from './breadcrumb.schema';
import { originFromUrl } from '@core-mkt/services/schema/schema-id.util';

import {
  ComponentTheme,
  ThemeInterface,
  ThemeParserService,
} from '@core-mkt/services/theme-parser/theme-parser.service';

@Component({
  selector: 'breadcrumb',
  templateUrl: './breadcrumb.component.html',
  styleUrls: ['./breadcrumb.component.scss'],
  standalone: true,
  imports: [FontAwesomeModule, QueryParamDirective],
})
export class BreadcrumbComponent implements OnInit, AfterViewInit {
  @Input() data: Breadcrumb;
  theme: ThemeInterface;
  faChevronRight = faChevronRight;
  border: boolean;
  tealLink: boolean;

  constructor(
    private themeParser: ThemeParserService,
    private accessibilityService: AccessibilityService,
    private schemaService: SchemaService,
    @Inject(DOCUMENT) private document: Document,
  ) {}

  get breadcrumbs(): BreadcrumbItem[] {
    return this.data.breadcrumbsList;
  }

  get backgroundColor(): string {
    if (
      this.data.backgroundColor &&
      (this.data.backgroundColor.includes('bg-white') || this.data.backgroundColor.includes('bg-ace-grey-100'))
    ) {
      this.tealLink = true;
    }
    return this.data.backgroundColor;
  }

  get textColor(): string {
    return this.data.textColor;
  }

  get hideOnMobile(): boolean {
    return this.data.hideOnMobile;
  }

  ngOnInit(): void {
    if (this.data.backgroundColor && this.backgroundColor.includes('bg-white')) {
      this.border = true;
    }
    const themeInput: ComponentTheme = {
      styles: this.data.styles,
      backgroundColor: this.data.backgroundColor ?? 'bg-vis-dark text-vis-reverse',
      backgroundImage: null,
      textColor: this.data.textColor,
      columnDistribution: null,
    };
    this.theme = this.themeParser.getThemeClasses(themeInput);
  }

  ngAfterViewInit(): void {
    const pageUrl = this.document.URL;
    // Derive the origin from document.URL, not document.location.origin: this hook
    // also runs SSR-side, where the injected DOCUMENT may have an unpopulated
    // `location` object that would throw. originFromUrl is SSR-safe and falls back
    // to '' (links stay relative) rather than breaking schema registration.
    const baseUrl = originFromUrl(pageUrl);
    this.schemaService.register(getBreadcrumbSchema(this.breadcrumbs, pageUrl, baseUrl));
  }

  /**
   * Gets the aria-label value to the anchor element
   * validating link url and calling getAriaLabel method from accessibilityService
   * @param link The string with the URL
   * @param text The string with the button text
   */
  getAriaLabelLink(link: string, text: string): string {
    if (link === '/') {
      return text;
    }
    return this.accessibilityService.getAriaLabel(link);
  }
}
